// @ts-strict
// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Description
//
// Include description of object & useful info here, please!
import Mousetrap from 'mousetrap';
import { merge } from 'shared/src/utils/merge';
import type { Accidental, LetterName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { letterNames } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { IntervalQuality } from 'shared/definitions/musicTheory/intervals';
import utils from '../utils';
import type { SolfegePanelOptions } from './solfegeLetterPanel';
import SolfegePanel from './solfegeLetterPanel';
import * as Font from '../uTheory.Font/font';
import type { PlaybackButtonsOptions } from '../uTheory.Interaction/playbackButtons';
import PlaybackButtons from '../uTheory.Interaction/playbackButtons';
import type { ChordIdButtonsOptions } from '../uTheory.Interaction/chordIdButtons';
import ChordIdButtons from '../uTheory.Interaction/chordIdButtons';
import sizeDiv from '../uTheory.Interaction/sizeDiv';

utils.createClass('uHideLastChild:last-child', 'display: none;');

export interface LetterPanelOptions {
  accidentalButtons: false | true | AccidentalButtonOptions;
  /**
   * Boolean, or string label to appear on the check button.
   */
  checkButton: boolean | string;
  chordIdButtons: false | ChordIdButtonsOptions;
  customButtons: false | LetterPanelButtonOrBRObject[];
  figuresButtons: false | FiguresButtonsOptions;
  hotKeys: boolean;
  intervalETButtons: false | IntervalETButtonsOptions;
  intervalQualityButtons: false | true | IntervalQuality[];
  keySigButtons: false | true | KeySigButtonOptions;
  letterButtons: boolean;
  numberButtons: false | true | NumberButtonOptions;
  numPad: false | NumPadOptions;
  playbackButtons: false | Partial<PlaybackButtonsOptions>;
  /**
   * CHORD quality buttons
   */
  qualityButtons: false | ChordQualityButtonOptions;
  rhythmPerformanceButtons: boolean;
  romanNumeralButtons: boolean;
  solfege: false | SolfegePanelOptions;
  trashButton: boolean;
}

interface AccidentalButtonOptions {
  /**
   * Don't allow pressing 'b' twice to get 'bb'
   */
  noDoubleTap?: boolean;
}

interface KeySigButtonOptions {
  /**
   * Default true
   */
  accidentals?: boolean;
  /**
   * Whether to listen for up/down to move the selected accidental
   * up or down on the staff. Default true.
   */
  arrowListeners?: boolean;
}

interface FiguresButtonsOptions {
  triads?: boolean;
  sevenths?: boolean;
}

interface NumberButtonOptions {
  from?: number;
  to?: number;
  level?: 1 | 2;
}

interface ChordQualityButtonOptions {
  sevenths?: boolean;
}

interface NumPadOptions {
  check?: boolean;
  trash?: boolean;
  hotKeys?: boolean;
}

interface IntervalETButtonsOptions {
  buttons?: string[];
  compound?: boolean;
  hotKeys?: boolean;
  useHalfWholeNames?: boolean;
}

type LetterPanelCallback = (value: string | number, viaKeyPress?: boolean) => void;

type LetterPanelPerspectiveName = 'widescreen' | 'landscape' | 'square' | 'portrait' | 'tall';

interface NumPadBreaksByRatio {
  fourByThree: HTMLBRElement[];
  threeByFour: HTMLBRElement[];
  sixByTwo: HTMLBRElement[];
}

export interface RhythmPerformanceButtons {
  start: [HTMLButtonElement];
  startCountoff: [HTMLButtonElement];
  tap: [HTMLButtonElement];
  repeat: [HTMLButtonElement, HTMLButtonElement];
  done: [HTMLButtonElement, HTMLButtonElement];
}

class LetterPanel {
  width: number;

  height: number;

  perspective?: LetterPanelPerspectiveName;

  options: LetterPanelOptions;

  private customButtons: LetterPanelButtonOrBRObject[] = [];

  public namedCustomButtons: {
    [buttonName: string]: HTMLButtonElement;
  } = {};

  private checkCallbacks: ((keypress?: boolean) => void)[] = [];

  private letterCallbacks: LetterPanelCallback[] = [];

  private customButtonCallbacks: LetterPanelCallback[] = [];

  private keySigCallbacks: LetterPanelCallback[] = [];

  private accidentalCallbacks: LetterPanelCallback[] = [];

  private qualityCallbacks: LetterPanelCallback[] = [];

  private intervalQualityCallbacks: LetterPanelCallback[] = [];

  private numberCallbacks: LetterPanelCallback[] = [];

  private figuresCallbacks: LetterPanelCallback[] = [];

  private romanNumeralCallbacks: LetterPanelCallback[] = [];

  /**
   * The span element to hold an accidental on any letter button.
   */
  private letterAccidentalText: HTMLSpanElement[] = [];

  /**
   * Holder for an answer of a letter name
   */
  private letterName: false | LetterName = false;

  /**
   * Holder for an answer's accidental name
   */
  private accidentalName: Accidental = '';

  /**
   * The accidental that is applied to all letters buttons. When set to 'b', for
   * instance, letter buttons appear as Cb, Db, etc...
   */
  private currentAccidental: Accidental = '';

  playbackButtons?: PlaybackButtons;

  chordIdButtons?: ChordIdButtons;

  solfege?: SolfegePanel;

  trashButton?: HTMLButtonElement;

  /**
   * A <br /> placed between F and G in letter buttons, and visible
   * when available width is below 500px.
   */
  private letterBreak?: HTMLBRElement;

  /**
   * Numbers 1-16, reporting values as strings.
   */
  private numbersButtons?: (HTMLButtonElement | HTMLBRElement)[];

  /**
   * Buttons for the 5 accidentals -- publically accessible because
   * solfegeThenStaffDictation reaches directly into this.
   */
  public accidentalButtons?: HTMLButtonElement[];

  public rhythmPerformanceButtons?: RhythmPerformanceButtons;

  // Public constructor
  constructor(public lettersDiv: HTMLDivElement, options: Partial<LetterPanelOptions>) {
    this.width = lettersDiv.clientWidth;
    this.height = 40; // just a holder...

    this.options = {
      accidentalButtons: false,
      checkButton: false,
      chordIdButtons: false,
      customButtons: false,
      figuresButtons: false,
      hotKeys: true,
      intervalETButtons: false,
      intervalQualityButtons: false,
      keySigButtons: false,
      letterButtons: false,
      numberButtons: false,
      numPad: false,
      playbackButtons: false,
      qualityButtons: false,
      rhythmPerformanceButtons: false,
      romanNumeralButtons: false,
      solfege: false,
      trashButton: false,
    };
    merge(this.options, options);

    if (this.options.letterButtons) {
      // Provide cache for letter & accidental answers:
      this.letterName = false;
      this.accidentalName = '';
      // Initialize each letter button:
      letterNames.forEach((letter) => {
        this.createLetterButton(letter);
      });
    }

    if (this.options.playbackButtons) {
      this.playbackButtons = new PlaybackButtons(
        this.lettersDiv,
        this.options.playbackButtons,
        this
      );
    }

    if (this.options.chordIdButtons) {
      this.chordIdButtons = new ChordIdButtons(this, this.options.chordIdButtons);
    }

    if (this.options.romanNumeralButtons) {
      this.addBr(this.options.letterButtons);
      this.createRomanNumeralButtons();
    }

    // Chord quality buttons M, m, +, o, etc...
    if (this.options.qualityButtons) {
      this.addBr(this.options.romanNumeralButtons || this.options.letterButtons);
      this.createQualityButtons(this.options.qualityButtons);
    }

    if (this.options.numberButtons) {
      const numberButtonOptions = utils.isObject(this.options.numberButtons)
        ? this.options.numberButtons
        : {};
      this.createNumberButtons(numberButtonOptions);
    }

    if (this.options.intervalQualityButtons) {
      this.addBr(!!this.options.numberButtons);
      this.createIntervalQualityButtons(this.options.intervalQualityButtons);
    }

    if (this.options.intervalETButtons) {
      this.createIntervalETButtons(this.options.intervalETButtons);
    }

    if (this.options.figuresButtons) {
      this.addBr(this.options.romanNumeralButtons || this.options.letterButtons);
      this.createFiguresButtons(this.options.figuresButtons);
    }

    if (this.options.accidentalButtons) {
      this.addBr(this.options.letterButtons);
      this.createAccidentalButtons();
    }

    if (this.options.keySigButtons) {
      this.addBr(
        !!this.options.letterButtons ||
          this.options.numberButtons !== false ||
          !!this.options.qualityButtons
      );
      this.createKeySigButtons(this.options.keySigButtons);
    }

    if (this.options.rhythmPerformanceButtons) {
      this.createRhythmPerformanceButtons();
    }

    if (this.options.numPad) this.createNumPad(this.options.numPad);

    if (this.options.customButtons) {
      this.createCustomButtons(this.options.customButtons);
    }

    if (this.options.solfege) {
      this.solfege = new SolfegePanel(this, this.options.solfege);
    }

    if (this.options.trashButton) {
      const button = this.createCustomButton({
        text: Font.toHTML('symbol', 'trash'),
        value: 'trash',
        hotKey: ['backspace', 'del'],
      });
      this.trashButton = button as HTMLButtonElement;
    }

    if (this.options.checkButton) {
      this.createCheckButton();
    }
  }

  responsiveResize(height = this.height, div = this.lettersDiv) {
    return this.resize(height, div);
  }

  /**
   * @param {number} availableHeight
   * @param {HTMLDivElement} div
   * @param {?string} perspective forces a perspective on the resize.
   */
  resize(
    availableHeight = this.height,
    div = this.lettersDiv,
    perspective?: LetterPanelPerspectiveName
  ) {
    //      console.log("letters resizing to " + availableHeight);
    div.style.minHeight = 'none';
    div.style.whiteSpace = 'nowrap';
    let availableWidth;
    if (div.parentElement) {
      availableWidth = div.parentElement.offsetWidth;
    } else {
      availableWidth = 500; // good guess
    }

    // If we're shorter than 500 wide, we need more layout space for letters:
    if (this.letterBreak) {
      this.letterBreak.style.display = availableWidth < 500 ? 'block' : 'none';
    }

    // If we have custom buttons w/ perspective elements, let's show or hide
    // their line breaks, etc...:
    this.showOrHideByAspectRatio(availableWidth, availableHeight, perspective);

    // If we have a numPad, set up its dimensions:
    if (this.options.numPad) this.resizeNumPad(availableWidth, availableHeight);

    // If there are letters, set to the widest letter:
    let accidental;
    if (this.options.letterButtons) {
      accidental = typeof this.currentAccidental === 'undefined' ? '' : this.currentAccidental;
      this.setLettersAccidental('bb');
    }

    // Now size up & measure it:
    const metrics = sizeTextDivViaButtons(availableWidth, availableHeight, div);

    // Add padding:
    addPaddingToButtons(availableWidth, div);

    // Lock down our div size:
    // div.style.height = `${metrics.height}px`;
    this.height = metrics.height;

    this.setLettersAccidental(accidental);

    return availableHeight - metrics.height;
  }

  /**
   * @param {number} width
   * @param {number} height
   * @param {?string} perspective forces a perspective
   */
  showOrHideByAspectRatio(width: number, height: number, perspective?: LetterPanelPerspectiveName) {
    const aspectLabels: Record<LetterPanelPerspectiveName, number> = {
      widescreen: 4, // 4:1
      landscape: 2, // 2:1
      square: 1, // 1:1
      portrait: 0.5, // 1:2
      tall: 0.25, // 1:4
    };
    const aspectRatios = [4, 2, 1, 0.5, 0.25];
    const goal = width / height;
    const closest = aspectRatios.reduce((prev, curr) =>
      Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev
    );
    perspective =
      perspective || (utils.arraySearch(closest, aspectLabels) as LetterPanelPerspectiveName);
    this.perspective = perspective;

    this.customButtons.forEach((buttonObj) => {
      if (!buttonObj.perspective) return;
      const perspectives = utils.isArray(buttonObj.perspective)
        ? buttonObj.perspective
        : [buttonObj.perspective];

      buttonObj.el!.style.display = perspectives.indexOf(perspective!) !== -1 ? 'inline' : 'none';
    });
  }

  resizeNumPad(width: number, height: number) {
    // If we're numPad, setup line breaks as desired:
    const aspectRatios = [10, 3, 0.75, 1.33];
    const goal = width / height;
    const closest = aspectRatios.reduce((prev, curr) =>
      Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev
    );

    const showBreaks = (bool: boolean, propName: keyof NumPadBreaksByRatio) => {
      this.numPadBreaks?.[propName].forEach((br) => {
        br.style.display = bool ? 'inline' : 'none';
      });
    };

    showBreaks(false, 'fourByThree');
    showBreaks(false, 'threeByFour');
    showBreaks(false, 'sixByTwo');

    switch (closest) {
      case 3:
        showBreaks(true, 'sixByTwo');
        break;
      case 0.75:
        showBreaks(true, 'threeByFour');
        break;
      case 1.33:
        showBreaks(true, 'fourByThree');
        break;
      default:
        break;
    }
  }

  private createLetterButton(letter: LetterName, accidental: Accidental = '') {
    const button = document.createElement('button');
    const text = document.createTextNode(letter);
    button.classList.add('accidentals');
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');
    button.appendChild(text);

    const accidentalHTML = Font.toHTML('accidental', accidental, { ariaSpaceBefore: true });
    const accidentalNode = document.createElement('span');
    accidentalNode.innerHTML = accidentalHTML;
    button.appendChild(accidentalNode);

    this.letterAccidentalText.push(accidentalNode);

    addButtonListeners(button, () => {
      this.letter(letter);
    });

    if (this.options.hotKeys) {
      const lowerCase = letter.toLowerCase();
      const keypress = true;
      Mousetrap.bind(letter, () => {
        this.letter(letter, keypress);
      });
      if (lowerCase !== 'b' || this.options.accidentalButtons === false) {
        Mousetrap.bind(lowerCase, () => {
          this.letter(letter, keypress);
        });
      }
    }
    this.lettersDiv.appendChild(button);

    if (letter === 'F') {
      const br = document.createElement('br');
      br.style.display = 'none';
      this.lettersDiv.appendChild(br);
      this.letterBreak = br;
    }
  }

  setLettersAccidental(accidental: Accidental = '') {
    this.currentAccidental = accidental;
    const accidentalHTML = Font.toHTML('accidental', accidental, { ariaSpaceBefore: true });
    this.letterAccidentalText.forEach((node) => {
      node.innerHTML = accidentalHTML;
    }, this);
  }

  createRomanNumeralButtons() {
    const I = Font.toHTML('romanNumeral', 'I');
    const V = Font.toHTML('romanNumeral', 'V');
    const im = Font.toHTML('romanNumeral', 'i');
    const v = Font.toHTML('romanNumeral', 'v');
    const trash = Font.toHTML('symbol', 'trash');

    const romans = [
      {
        html: I,
        value: 'I',
        hotKey: 'I',
      },
      {
        html: V,
        value: 'V',
        hotKey: 'V',
      },
      {
        html: im,
        value: 'i',
        hotKey: 'i',
      },
      {
        html: v,
        value: 'v',
        hotKey: 'v',
      },
      {
        html: trash,
        value: 'delete',
        hotKey: ['del', 'backspace'],
      },
    ];

    romans.forEach((rn) => this.createRomanNumeralButton(rn.html, rn.value, rn.hotKey));
  }

  createRomanNumeralButton(HTML: string, value: string, keyCatchValue: string | string[]) {
    const button = document.createElement('button');
    button.innerHTML = HTML;

    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('accidentals');
    button.classList.add('uLetterPanelButton');

    addButtonListeners(button, () => {
      this.romanNumeral(value, false);
    });

    if (this.options.hotKeys) {
      Mousetrap.bind(keyCatchValue, (e) => {
        e.preventDefault();
        this.romanNumeral(value, true);
      });
    }

    this.lettersDiv.appendChild(button);
  }

  createQualityButtons(options: ChordQualityButtonOptions) {
    const dom = Font.toHTML('figuredBass', '7');
    const maj = Font.toHTML('quality', 'M');
    const min = Font.toHTML('quality', 'm');
    const aug = Font.toHTML('quality', '+');
    const dim = Font.toHTML('quality', 'o');
    const halfDim = Font.toHTML('quality', 'o/');

    const buttons = [
      { label: maj, value: 'M', hotKey: 'M' },
      { label: min, value: 'm', hotKey: 'm' },
      { label: aug, value: '+', hotKey: '+' },
      { label: dim, value: 'o', hotKey: 'd' },
    ];

    if (options.sevenths === true) {
      buttons.push(
        {
          label: dom,
          value: '7',
          hotKey: '7',
        },
        {
          label: halfDim,
          value: 'o/7',
          hotKey: 'h',
        }
      );
    }

    buttons.forEach(({ label, value, hotKey }) => {
      this.createQualityButton(label, value, hotKey);
    });
  }

  createQualityButton(HTML: string, value: string, keyCatchValue: string) {
    const button = document.createElement('button');
    button.innerHTML = HTML;

    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('accidentals');
    button.classList.add('uLetterPanelButton');

    addButtonListeners(button, () => {
      this.quality(value, false);
    });

    if (this.options.hotKeys) {
      Mousetrap.bind(keyCatchValue, (e) => {
        e.preventDefault();
        this.quality(value, true);
      });
    }

    this.lettersDiv.appendChild(button);
  }

  createIntervalETButtons({
    buttons,
    compound = false,
    hotKeys = true,
    useHalfWholeNames = false,
  }: IntervalETButtonsOptions) {
    if (!utils.isArray(buttons))
      buttons = ['m2', 'M2', 'm3', 'M3', 'P4', 'd5', 'P5', 'm6', 'M6', 'm7', 'M7', 'P8'];
    const brBreaks = {
      // widescreen: all on same line
      // landscape: break into 2 lines >= 6 buttons;
      landscape: [[6, 2]],
      // square: break into 3 lines if >= 9 buttons; 2 lines if >= 5
      square: [
        [12, 4],
        [9, 3],
        [5, 2],
      ],
      // portrait: break into 3 lines if >= 5;
      portrait: [
        [11, 4],
        [5, 3],
        [4, 2],
      ],
      // tall: break into 2 lines if >= 4, 3 lines if >= 5; 4 lines if >= 12;
      tall: [
        [12, 6],
        [11, 4],
        [5, 3],
        [4, 2],
      ],
    };

    const br = (buttonIndex: number) => {
      // Don't break after the last one... but do if we're compound
      if (buttonIndex === buttons!.length - (compound ? 0 : 1)) return;
      utils.forEach(
        brBreaks,
        (orientation: [number, number][], orientationName: LetterPanelPerspectiveName) => {
          for (let i = 0; i < orientation.length; i++) {
            const breakPoint = orientation[i];
            const minButtons = breakPoint![0];
            const totalLines = breakPoint![1];
            const lineNeedsBreak = buttons!.length >= minButtons;
            const needBrNow = (buttonIndex + 1) % Math.ceil(buttons!.length / totalLines) === 0;
            if (lineNeedsBreak && needBrNow) {
              this.createCustomButton({
                perspective: [orientationName as LetterPanelPerspectiveName],
                tagName: 'br',
                class: orientationName,
              });
            }
            if (lineNeedsBreak) break;
          }
        }
      );
    };

    let compoundState = false;
    const switchCompound = () => {
      compoundState = !compoundState;
      const buttonObjs = this.customButtons.filter(
        (obj): obj is LetterPanelButtonObject => 'group' in obj && obj.group === 'intervalETButtons'
      );
      buttonObjs.forEach((button) => {
        button.el!.innerHTML = `${button.value}`;
      });
      const oldHeight = this.height;
      this.resize(oldHeight, undefined, this.perspective);
      this.height = Math.max(oldHeight, this.height);
    };

    buttons
      .map((button) => ({
        quality: button[0],
        simpleGeneric: button[1],
        compoundGeneric: `${Number(button[1]) + 7}`,
      }))
      .forEach((button, index) => {
        const quality = button.quality!;
        const simpleGeneric = button.simpleGeneric;
        const compoundGeneric = button.compoundGeneric;

        this.createCustomButton({
          get text() {
            if ((this as LetterPanelButtonObject).value === 'm2' && useHalfWholeNames)
              return 'Half Step';
            if ((this as LetterPanelButtonObject).value === 'M2' && useHalfWholeNames)
              return 'Whole Step';
            return this.value;
          },
          get value() {
            return quality + (compoundState ? compoundGeneric : simpleGeneric);
          },
          group: 'intervalETButtons',
        });
        br(index);
      });

    if (compound) {
      this.createCustomButton({
        text: '&plusmn;8<small>ve</small>',
        value: 'switchCompound',
        callback: switchCompound,
        hotKey: ['+', '-'],
      });
    }

    // make the hotKeys:
    if (hotKeys) {
      if (useHalfWholeNames) {
        Mousetrap.bind(['h', 'H'], (e) => {
          e.preventDefault();
          this.customButtonEvent('m2', true);
        });
        Mousetrap.bind(['w', 'W'], (e) => {
          e.preventDefault();
          this.customButtonEvent('M2', true);
        });
      } else {
        // If we're not in half-whole mode:
        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 'm', 'M', 'A', 'd', 'P'].forEach((buttonValue) => {
          Mousetrap.bind(`${buttonValue}`, (e) => {
            e.preventDefault();
            this.customButtonEvent(buttonValue, true);
          });
        });

        Mousetrap.bind('enter', (e) => {
          e.preventDefault();
          this.customButtonEvent('check', true);
        });
        Mousetrap.bind(['backspace', 'delete'], (e) => {
          e.preventDefault();
          this.customButtonEvent('del', true);
        });
      }
    }
  }

  createIntervalQualityButtons(qualities: false | true | IntervalQuality[]) {
    if (!Array.isArray(qualities)) qualities = ['d', 'm', 'P', 'M', 'A'];
    const qualityLabels = ['d', 'm', 'P', 'M', 'A'];
    const qualityValues = ['d', 'm', 'P', 'M', 'A'];
    const qualityKeyCatchValues = [['d', 'D'], 'm', ['P', 'p'], 'M', ['A', 'a']];

    qualities.forEach((quality) => {
      const index = qualityLabels.indexOf(quality);
      this.createIntervalQualityButton(
        qualityLabels[index]!,
        qualityValues[index]!,
        qualityKeyCatchValues[index]!
      );
    });
    this.createCustomButton({
      tagName: 'br',
      class: 'uHideLastChild',
    });
  }

  createIntervalQualityButton(label: string, value: string, keyCatchValue: string | string[]) {
    const button = document.createElement('button');
    const text = document.createTextNode(label);

    button.appendChild(text);
    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');

    addButtonListeners(button, () => {
      this.intervalQuality(value, false);
    });

    if (this.options.hotKeys) {
      Mousetrap.bind(keyCatchValue, (e) => {
        e.preventDefault();
        this.intervalQuality(value, true);
      });
    }

    this.lettersDiv.appendChild(button);
    return button;
  }

  createNumberButtons(options: NumberButtonOptions) {
    const numbersDiv = document.createElement('div');
    numbersDiv.style.margin = '0';
    numbersDiv.style.padding = '0';
    numbersDiv.style.border = '0';
    //      this.lettersDiv.appendChild(this.numbersDiv);

    let maxNumber = options.level === 2 ? 16 : 8;

    this.numbersButtons = [];

    let i = options.from !== undefined ? options.from : 1;
    maxNumber = options.to !== undefined ? options.to : maxNumber;

    for (i; i <= 16; i++) {
      const str = `${i}`;
      this.numbersButtons.push(this.createNumberButton(str, str, str, i <= maxNumber));
    }

    if (options.level === 2) {
      this.breakNumbersButtons(8);
      this.breakNumbersButtons(12);
    }
  }

  private breakNumbersButtons(index: number) {
    const br = document.createElement('br');
    this.lettersDiv.insertBefore(br, this.numbersButtons![index]!);
    this.numbersButtons!.push(br);
  }

  public switchNumbersButtons(level: 1 | 2) {
    const buttons = [...this.lettersDiv.childNodes];
    let nextSibling: HTMLElement;
    this.numbersButtons!.forEach((button) => {
      if (buttons.indexOf(button) !== -1) {
        nextSibling = button.nextSibling as HTMLElement;
        this.lettersDiv.removeChild(button);
      }
    });

    let i;
    switch (level) {
      case 1:
        for (i = 0; i < 8; i++) {
          this.lettersDiv.insertBefore(this.numbersButtons![i]!, nextSibling!);
        }
        break;
      case 2:
        for (i = 8; i < 16; i++) {
          this.lettersDiv.insertBefore(this.numbersButtons![i]!, nextSibling!);
        }
        break;
    }

    if (typeof this.height !== 'undefined') {
      this.responsiveResize(this.height, this.lettersDiv);
    }
  }

  createNumberButton(
    label: string,
    value: string,
    keyCatchValue: string | string[],
    visible: boolean
  ) {
    const button = document.createElement('button');
    const text = document.createTextNode(label);

    button.appendChild(text);
    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');

    addButtonListeners(button, () => {
      this.number(value, false);
    });

    if (this.options.hotKeys && Number(keyCatchValue) < 10) {
      Mousetrap.bind(keyCatchValue, (e) => {
        e.preventDefault();
        this.number(value, true);
      });
    }

    if (visible) this.lettersDiv.appendChild(button);
    return button;
  }

  createFiguresButtons(options: FiguresButtonsOptions) {
    const rootTriad = Font.toHTML('figuredBass', '53');
    const firstTriad = Font.toHTML('figuredBass', '63');
    const secondTriad = Font.toHTML('figuredBass', '64');
    const rootSeventh = Font.toHTML('figuredBass', '7');
    const firstSeventh = Font.toHTML('figuredBass', '65');
    const secondSeventh = Font.toHTML('figuredBass', '43');
    const thirdSeventh = Font.toHTML('figuredBass', '42');
    const triads: [string[], string[], string[]] = [
      [rootTriad, firstTriad, secondTriad],
      ['53', '63', '64'],
      ['53', '63', '64'],
    ];
    const sevenths: [string[], string[], string[]] = [
      [rootSeventh, firstSeventh, secondSeventh, thirdSeventh],
      ['7', '65', '43', '42'],
      ['7', '65', '43', '42'],
    ];

    const createButtons = (array: [string[], string[], string[]]) => {
      for (let i = 0; i < array[0].length; i++) {
        this.createFiguresButton(array[0][i]!, array[1][i]!, array[2][i]!);
      }
    };

    if ((options.triads === true && options.sevenths === true) || options === true) {
      createButtons(triads);
      createButtons(sevenths);
    } else if (options.triads === true) {
      createButtons(triads);
    } else if (options.sevenths === true) {
      createButtons(sevenths);
    }
  }

  createFiguresButton(HTML: string, value: string, keyCatchValue: string) {
    const button = document.createElement('button');
    button.innerHTML = HTML;
    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('accidentals');
    button.classList.add('uLetterPanelButton');

    addButtonListeners(button, () => {
      this.figures(value, false);
    });

    if (this.options.hotKeys) {
      Mousetrap.bind(keyCatchValue, (e) => {
        e.preventDefault();
        this.figures(value, true);
      });
    }

    this.lettersDiv.appendChild(button);
  }

  createAccidentalButtons() {
    const accidentalLabels = [
      Font.toHTML('accidental', 'bb'),
      Font.toHTML('accidental', 'b'),
      Font.toHTML('accidental', 'n'),
      Font.toHTML('accidental', '#'),
      Font.toHTML('accidental', 'x'),
    ];
    const accidentalValues: Accidental[] = ['bb', 'b', '', '#', 'x'];
    const accidentalKeyCatchValues = ['v', 'b', 'n', '#', 'x'];
    const accidentalButtons: HTMLButtonElement[] = [];

    for (let i = 0; i < accidentalLabels.length; i++) {
      const button = this.createAccidentalButton(
        accidentalLabels[i]!,
        accidentalValues[i]!,
        accidentalKeyCatchValues[i]!
      );
      accidentalButtons.push(button);
    }

    this.accidentalButtons = accidentalButtons;
  }

  createAccidentalButton(HTML: string, value: Accidental, keyCatchValue: string) {
    const button = document.createElement('button');
    button.innerHTML = HTML;
    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');
    button.classList.add('uLetterPanelAccidentalButton');
    addButtonListeners(button, () => {
      this.accidental(value, false);
    });

    if (this.options.hotKeys) {
      Mousetrap.bind(keyCatchValue, () => {
        this.accidentalKeyPress(value, true);
      });
    }

    this.lettersDiv.appendChild(button);
    return button;
  }

  /**
   * Creates key sig buttons and registers listeners.
   *
   * @param {object | true } [keySigButtonOptions]
   * @param {boolean} [keySigButtonOptions.accidentals] default true
   * @param {boolean} [keySigButtonOptions.arrowListeners] default true
   */
  createKeySigButtons(options: KeySigButtonOptions | true) {
    const keySigButtonOptions = {
      accidentals: true,
      arrowListeners: true,
      ...(options === true ? {} : options),
    };

    [
      {
        text: Font.toHTML('accidental', 'b'),
        value: 'b',
        hotKey: 'b',
      },
      {
        text: Font.toHTML('accidental', '#'),
        value: '#',
        hotKey: '#',
      },
      {
        text: Font.toHTML('symbol', 'trash'),
        value: 'delete',
        hotKey: ['del', 'backspace'],
      },
    ]
      .map(({ text, value, hotKey }) => {
        const isAccidental = value === '#' || value === 'b';
        if (isAccidental && !keySigButtonOptions.accidentals) return false;
        return this.createKeySigButton(text, value, hotKey);
      })
      .filter((b) => b);

    if (keySigButtonOptions.arrowListeners) this.createKeySigHotKeys();
  }

  createKeySigButton(HTML: string, value: string, keyCatchValue: string | string[]) {
    const button = document.createElement('button');
    button.innerHTML = HTML;
    // button.style.fontSize = '4vw';
    button.classList.add('uButton');
    button.classList.add('accidentals');
    button.classList.add('uLetterPanelButton');
    addButtonListeners(button, () => {
      this.keySig(value, false);
    });

    if (this.options.hotKeys) {
      Mousetrap.bind(keyCatchValue, (evt) => {
        evt.preventDefault();
        this.keySig(value, true);
      });
    }

    this.lettersDiv.appendChild(button);
    return button;
  }

  createKeySigHotKeys() {
    // Key sig arrow manipulation keys:
    if (this.options.hotKeys) {
      this.addKeySigHotKeyListener('left', 'left');
      this.addKeySigHotKeyListener('right', 'right');
      this.addKeySigHotKeyListener('up', 'up');
      this.addKeySigHotKeyListener('down', 'down');
    }
  }

  addKeySigHotKeyListener(hotKey: string | string[], value: string) {
    Mousetrap.bind(hotKey, (evt) => {
      evt.preventDefault();
      this.keySig(value, true);
    });
  }

  createCheckButton() {
    const button = document.createElement('button');
    let text = document.createTextNode('Check');
    if (typeof this.options.checkButton === 'string')
      text = document.createTextNode(this.options.checkButton);
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');
    button.appendChild(text);
    // button.style.fontSize = '4vw';
    addButtonListeners(button, () => {
      this.check();
    });

    if (this.options.hotKeys) {
      const keypress = true;
      Mousetrap.bind(['enter'], (e) => {
        e.preventDefault();
        this.check(keypress);
      });
    }
    this.lettersDiv.appendChild(button);
  }

  createRhythmPerformanceButtons() {
    const tapText = utils.isTouch() ? 'Tap' : 'Click or Space';

    const rpb: RhythmPerformanceButtons = {
      start: [
        this.createCustomButton({
          text: 'Start',
          value: 'start',
        }) as HTMLButtonElement,
      ],

      startCountoff: [
        this.createCustomButton({
          text: 'Start Countoff',
          value: 'start countoff',
        }) as HTMLButtonElement,
      ],
      tap: [
        this.createCustomButton({
          text: tapText,
          down: 'noteOn',
          up: 'noteOff',
          allowFastClicks: true,
          downUpNotifications: true,
        }) as HTMLButtonElement,
      ],
      repeat: [
        this.createCustomButton({
          text: 'Slow Practice',
          value: 'startPractice',
        }) as HTMLButtonElement,
        this.createCustomButton({
          text: 'Try Again',
          value: 'start',
        }) as HTMLButtonElement,
      ],

      done: [
        this.createCustomButton({
          text: 'Again',
          value: 'start',
        }) as HTMLButtonElement,
        this.createCustomButton({
          text: 'Next',
          value: 'next',
        }) as HTMLButtonElement,
      ],
    };

    rpb.tap[0]!.style.width = '98%';

    this.rhythmPerformanceButtons = rpb;

    this.switchRhythmView('start');
  }

  switchRhythmView(view: keyof RhythmPerformanceButtons) {
    const rpb = this.rhythmPerformanceButtons!;
    // View options:
    // - start [ START ]
    // - tap [ TAP ] or on computer [ TAP HERE OR SPACE BAR ]
    // - repeat [ TRY AGAIN ][ PRACTICE ]
    // - done [ AGAIN ][ NEXT ]
    const minHeight = this.lettersDiv.clientHeight;
    const originalHeight = this.height;
    this.lettersDiv.style.minHeight = `${minHeight}px`;
    // Get rid of all the buttons:
    while (this.lettersDiv.firstChild) {
      this.lettersDiv.removeChild(this.lettersDiv.firstChild);
    }

    rpb[view].forEach((button) => {
      this.lettersDiv.appendChild(button);
    });

    rpb.tap[0].style.width = 'auto';
    if (typeof this.height !== 'undefined') {
      this.lettersDiv.style.minHeight = 'none';
      this.responsiveResize(this.height, this.lettersDiv);
      this.lettersDiv.style.minHeight = `${minHeight}px`;
    }
    this.height = Math.max(this.height, originalHeight);
    rpb.tap[0].style.width = '98%';
  }

  numPadBreaks?: NumPadBreaksByRatio;

  createNumPad(options: NumPadOptions) {
    const { lettersDiv } = this;
    const addBreak = () => {
      const br = document.createElement('br');
      br.style.display = 'none';
      lettersDiv.appendChild(br);
      return br;
    };

    const fourByThreeBreaks = [4, 8];
    const sixByTwoBreaks = [6];
    const threeByFourBreaks = [3, 6, 9];

    this.numPadBreaks = {
      fourByThree: [],
      threeByFour: [],
      sixByTwo: [],
    };

    const numButtons = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
    numButtons.forEach((num) => {
      this.createCustomButton({
        text: `${num}`,
        value: num,
        hotKey: `${num}`,
      });
      if (fourByThreeBreaks.indexOf(num) !== -1) {
        this.numPadBreaks!.fourByThree.push(addBreak());
      }
      if (threeByFourBreaks.indexOf(num) !== -1) {
        this.numPadBreaks!.threeByFour.push(addBreak());
      }
      if (sixByTwoBreaks.indexOf(num) !== -1) {
        this.numPadBreaks!.sixByTwo.push(addBreak());
      }

      if (options.hotKeys) {
        Mousetrap.bind(`${num}`, () => {
          this.customButtonEvent(`${num}`);
        });
      }
    }, this);

    if (options.check && options.trash) {
      this.createCustomButtons([
        {
          text: Font.toHTML('symbol', 'trash'),
          value: 'trash',
          hotKey: ['del', 'backspace'],
        },
        {
          text: Font.toHTML('symbol', 'check'),
          value: 'check',
          hotKey: 'enter',
        },
      ]);
    } else if (options.check) {
      this.createCustomButton({
        text: 'Check',
        value: 'check',
        hotKey: 'enter',
      });
    }

    this.createCustomButton({
      tagName: 'br',
      class: 'uHideLastChild',
    });
  }

  createCustomButtons(buttonObjectsArray: LetterPanelButtonOrBRObject[]) {
    buttonObjectsArray.forEach((buttonObject) => {
      this.createCustomButton(buttonObject);
    });
  }

  /**
   * @param {object} buttonObject
   * @param {string} buttonObject.text label
   * @param {string} buttonObject.value stringSentToCallback
   * @param {string} [buttonObject.tagName] 'br' or omit for button.
   * @param {Function} [buttonObject.callback]  override default customButtonCallback with function
   * @param {string|string[]} [buttonObject.hotKey]
   * @param {string} [buttonObject.class] css class string
   * @param {string} [buttonObject.variable] save element as letterPanel.customButtons[variable],
   * @param {string|string[]} [buttonObject.perspective] values can be:
   *    widescreen 4:1, landscape 2:1, square 1:1, portrait 1:2, tall 1:4
   * @param {boolean} [buttonObject.doNotAppend] don't append to doc.
   * @param {boolean} [buttonObject.doNotPush] don't push to this.customButtons
   * @param {boolean} [buttonObject.allowFastClicks] don't disable click event on double/triple clicks
   * @returns {HTMLButtonElement}
   */
  createCustomButton(buttonObject: LetterPanelButtonOrBRObject) {
    if (isLetterPanelBRObject(buttonObject)) {
      const br = document.createElement('br');
      if (!buttonObject.doNotAppend) this.lettersDiv.appendChild(br);
      buttonObject.el = br;
      if (buttonObject.class) {
        br.classList.add(buttonObject.class);
      }
      if (!buttonObject.doNotPush) this.customButtons.push(buttonObject);
      return br;
    }

    if (!buttonObject.downUpNotifications && typeof buttonObject.value === 'undefined') {
      console.error(
        'buttonObject.value must be defined if buttonObject.downUpNotifications is not set.'
      );
    }

    const button = document.createElement('button');
    if (buttonObject.variable) {
      if (utils.isNumeric(buttonObject.variable)) {
        throw new Error(
          'Do not use a numeric value ' +
            'for a custom button object variable name. It may ' +
            'overwrite a value in customButtons array.'
        );
      }
      this.namedCustomButtons[buttonObject.variable] = button;
    }

    //      var text = document.createTextNode(buttonObject.text);
    //      button.appendChild(text);
    button.innerHTML = buttonObject.text;

    if (typeof buttonObject.class !== 'undefined') {
      button.classList.add(buttonObject.class);
    }
    button.classList.add('uButton');
    button.classList.add('uLetterPanelButton');
    // button.style.fontSize = '4vw';

    if (buttonObject.downUpNotifications !== true) {
      addButtonListeners(
        button,
        () => {
          if (buttonObject.callback) {
            buttonObject.callback(buttonObject.value!);
          } else {
            this.customButtonEvent(buttonObject.value!, false);
          }
        },
        buttonObject
      );
    } else {
      button.addEventListener('touchstart', (evt) => {
        evt.preventDefault();
        this.customButtonEvent(buttonObject.down!, false);
      });
      button.addEventListener('touchend', (evt) => {
        evt.preventDefault();
        this.customButtonEvent(buttonObject.up!, false);
      });
      button.addEventListener('mousedown', (evt) => {
        evt.preventDefault();
        this.customButtonEvent(buttonObject.down!, false);
      });
      button.addEventListener('mouseup', (evt) => {
        evt.preventDefault();
        this.customButtonEvent(buttonObject.up!, false);
      });
    }
    // If it has a hotkey, set it:
    if (typeof buttonObject.hotKey !== 'undefined') {
      Mousetrap.bind(buttonObject.hotKey, (e) => {
        e.preventDefault();
        if (buttonObject.callback) {
          buttonObject.callback(buttonObject.value!);
        } else {
          this.customButtonEvent(buttonObject.value!, true);
        }
      });
    }
    if (!buttonObject.doNotAppend) {
      this.lettersDiv.appendChild(button);
    }
    buttonObject.el = button;
    if (!buttonObject.doNotPush) this.customButtons.push(buttonObject);
    return button;
  }

  static wasDoubleClick(evt: MouseEvent) {
    return evt.detail > 1;
  }

  addLetterCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.letterCallbacks.push(callback.bind(thisArg));
    } else this.letterCallbacks.push(callback);
  }

  // When a letter button is pressed, the letter function is
  // triggered:
  private letter(letter: LetterName, keypress?: boolean) {
    this.letterName = letter;
    // And send it to any letterCallbacks:
    this.letterCallbacks.forEach((callback) => {
      callback(letter, keypress);
    }, this);
  }

  // Clears letterName & accidentalName:
  clearNoteName() {
    this.letterName = false;
    this.accidentalName = '';
  }

  // Return cached letterName & accidentalName answer
  // or false if a letter has not been chosen.
  getNoteName() {
    if (typeof this.letterName !== 'undefined' && this.letterName !== false) {
      return this.letterName + this.accidentalName;
    }
    return false;
  }

  getPartialNoteName() {
    return this.letterName ? this.letterName + this.accidentalName : this.accidentalName;
  }

  addAccidentalCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.accidentalCallbacks.push(callback.bind(thisArg));
    } else this.accidentalCallbacks.push(callback);
  }

  // When an accidental button is pressed, this is called:
  private accidental(accidental: Accidental, keypress?: boolean) {
    this.accidentalName = accidental;
    // Send it to any accidentalCallbacks:
    this.accidentalCallbacks.forEach((callback) => {
      callback(accidental, keypress);
    }, this);
  }

  private accidentalKeyPress(accidental: Accidental, keypress?: boolean) {
    if (
      typeof this.options.accidentalButtons !== 'object' ||
      this.options.accidentalButtons.noDoubleTap !== true
    ) {
      if (accidental === 'b' && this.accidentalName === 'b') accidental = 'bb';
      if (accidental === '#' && this.accidentalName === '#') accidental = 'x';
    }

    this.accidental(accidental, keypress);
  }

  addKeySigCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.keySigCallbacks.push(callback.bind(thisArg));
    } else this.keySigCallbacks.push(callback);
  }

  private keySig(value: string, keypress?: boolean) {
    // values can be b (flat), # (sharp) or † (delete -- dagger)
    this.keySigCallbacks.forEach((callback) => {
      callback(value, keypress);
    }, this);
  }

  addRomanNumeralCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.romanNumeralCallbacks.push(callback.bind(thisArg));
    } else {
      this.romanNumeralCallbacks.push(callback);
    }
  }

  private romanNumeral(value: string | number, keypress?: boolean) {
    this.romanNumeralCallbacks.forEach((callback) => {
      callback(value, keypress);
    }, this);
  }

  addQualityCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.qualityCallbacks.push(callback.bind(thisArg));
    } else {
      this.qualityCallbacks.push(callback);
    }
  }

  private quality(value: string | number, keypress?: boolean) {
    this.qualityCallbacks.forEach((callback) => {
      callback(value, keypress);
    }, this);
  }

  addIntervalQualityCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.intervalQualityCallbacks.push(callback.bind(thisArg));
    } else {
      this.intervalQualityCallbacks.push(callback);
    }
  }

  private intervalQuality(value: string | number, keypress?: boolean) {
    this.intervalQualityCallbacks.forEach((callback) => {
      callback(value, keypress);
    }, this);
  }

  addNumberCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.numberCallbacks.push(callback.bind(thisArg));
    } else {
      this.numberCallbacks.push(callback);
    }
  }

  private number(value: string | number, keypress?: boolean) {
    this.numberCallbacks.forEach((callback) => {
      callback(value, keypress);
    }, this);
  }

  addFiguresCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.figuresCallbacks.push(callback.bind(thisArg));
    } else {
      this.figuresCallbacks.push(callback);
    }
  }

  private figures(value: string | number, keypress?: boolean) {
    this.figuresCallbacks.forEach((callback) => {
      callback(value, keypress);
    }, this);
  }

  addCustomButtonCallback(callback: LetterPanelCallback, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.customButtonCallbacks.push(callback.bind(thisArg));
    } else this.customButtonCallbacks.push(callback);
  }

  customButtonEvent(value: string | number, viaKeyPress: boolean = false) {
    // Send it to any accidentalCallbacks:
    this.customButtonCallbacks.forEach((callback) => {
      callback(value, viaKeyPress);
    }, this);
  }

  addCheckCallback(callback: (keypress?: boolean) => void, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.checkCallbacks.push(callback.bind(thisArg));
    } else this.checkCallbacks.push(callback);
  }

  check(keypress?: boolean) {
    // And send it to any letterCallbacks:
    this.checkCallbacks.forEach((callback) => {
      callback(keypress);
    }, this);
  }

  addBr(value: boolean) {
    if (value === true) {
      const br = document.createElement('br');
      this.lettersDiv.appendChild(br);
      return br;
    }
    return undefined;
  }
}

export default LetterPanel;

interface SharedButtonAndBRObjectOptions {
  /**
   * A css class string to add to the button
   */
  class?: string;
  /**
   * Save element as letterPanel.customButtons[variable]
   */
  variable?: string;
  /**
   * The perspectives at which a button should be visible. Values can be
   * widescreen (4:1), landscape (2:1), square (1:1), portrait (1:2) or tall
   * (1:4)
   */
  perspective?: LetterPanelPerspectiveName[];
  /**
   * Create the button, but don't append it to the lettersDiv.
   */
  doNotAppend?: boolean;
  /**
   * Create the button, but don't push it to letterPanel.customButtons.
   */
  doNotPush?: boolean;
  /**
   * Added automatically on button creation, and used for later styling/showing/hiding.
   */
  el?: HTMLButtonElement | HTMLBRElement;
}

interface LetterPanelBRObject extends SharedButtonAndBRObjectOptions {
  /**
   * 'br', or omit for a button
   */
  tagName: 'br';
}

export interface LetterPanelButtonObject extends SharedButtonAndBRObjectOptions {
  /**
   * The button's label. Omit if tagName === 'br'
   */
  text: string;
  /**
   * The value sent to the callback. Omit if tagName === 'br', or
   * downUpNotifications === true
   */
  value?: string | number;
  /**
   * The function to callback when the button is clicked. If not specified,
   * the buttons value is sent to each of letterPanel.customButtonCallbacks.
   */
  callback?: LetterPanelCallback;
  /**
   * The key (or keys), if any, that should trigger the button
   */
  hotKey?: string | string[];
  /**
   * Don't disable second click event on double clicks.
   */
  allowFastClicks?: boolean;
  /**
   * Useful for finding/grouping, but does not affect layout in any way.
   */
  group?: string;
  /**
   * Whether to dispatch callbacks on mouseDown/touchStart, mouseUp/touchEnd.
   * Used for rhythm performance/tap button.
   */
  downUpNotifications?: boolean;
  /**
   * Callback value for customButtonEvent when mouseDown/touchStart occurs, if downUpNotifications === true
   */
  down?: string;
  /**
   * Callback value for customButtonEvent when mouseUp/touchEnd occurs, if downUpNotifications === true
   */
  up?: string;
}

export type LetterPanelButtonOrBRObject = LetterPanelButtonObject | LetterPanelBRObject;

function isLetterPanelBRObject(obj: LetterPanelButtonOrBRObject): obj is LetterPanelBRObject {
  return 'tagName' in obj && obj.tagName === 'br';
}

function addButtonListeners(
  button: HTMLButtonElement,
  callback: () => void,
  buttonObject?: LetterPanelButtonObject
) {
  button.addEventListener('touchstart', (evt) => {
    evt.preventDefault();
    callback();
  });
  button.addEventListener('click', (evt) => {
    evt.preventDefault();
    const allowFastClicks = buttonObject && buttonObject.allowFastClicks;
    // if this was a double-click event, we don't need it
    if (!allowFastClicks && LetterPanel.wasDoubleClick(evt)) return;
    callback();
  });
}

function sizeTextDivViaButtons(width: number, height: number, div: HTMLDivElement) {
  const widthStyle = div.style.width;
  div.style.width = '';
  // const heightStyle = div.style.height;
  div.style.height = '';
  const displayStyle = div.style.display;
  div.style.display = 'inline-block';

  // console.log(`Sizing text div to width: ${width}, height: ${height}`);
  // console.log('div', div);
  // console.log(`Starting width: ${div.clientWidth}, starting height: ${div.clientHeight}`);

  // // start at a size that's probably too small:
  // let searchLow = 7;
  // let searchHigh = 100;
  // let searchPoint;
  // let fontSize = 12;

  const buttonsHtmlCollection = document.getElementsByClassName(
    'uLetterPanelButton'
  ) as HTMLCollectionOf<HTMLButtonElement>;
  const buttons = [...buttonsHtmlCollection];
  // console.log('buttons', buttons);

  function setSizeCallback(px: number) {
    buttons.forEach((button) => {
      div.style.fontSize = `${px}px`;
      button.style.fontSize = `${px}px`;
    });
  }

  // const setFontSize = px => {
  //   console.log(`setting to ${px}px`);
  //   buttons.forEach(button => {
  //     button.style['font-size'] = `${px}px`;
  //   });
  // };

  // Be sure padding is set to small... we'll expand later if room...
  buttons.forEach((button) => {
    button.style.padding = '0px 10px';
  });

  sizeDiv(width, height, div, { setSizeCallback, noWrap: true });

  div.style.width = widthStyle || '';
  // div.style.height = heightStyle || '';
  div.style.display = displayStyle || '';

  return {
    fontSize: div.style.fontSize,
    width: div.clientWidth,
    height: div.clientHeight,
  };
}

function addPaddingToButtons(availableWidth: number, div: HTMLDivElement) {
  const displayStyle = div.style.display;
  div.style.display = 'inline-block';
  const widthStyle = div.style.width;
  div.style.width = '';

  //      console.log("adding padding");
  const buttonsHtmlCollection = document.getElementsByClassName(
    'uLetterPanelButton'
  ) as HTMLCollectionOf<HTMLButtonElement>;
  const buttons = [...buttonsHtmlCollection];
  if (buttons.length === 0) return;
  let padding = 32;
  let escapeCount = 0;
  do {
    // GCR: This is a kluge, and you've fixed the bug that initially
    // required it, but let's leave it here just in case you're wrong
    // about having fixed it
    if (++escapeCount > 25) {
      console.log('WARNING: Infinite loop break in letterPanel');
      break;
    }
    padding -= 2;

    // eslint-disable-next-line @typescript-eslint/no-loop-func
    buttons.forEach((button) => {
      button.style.paddingLeft = `${padding}px`;
      button.style.paddingRight = `${padding}px`;
    });

    // console.log(`Padding ${padding} fits? ${div.clientWidth < availableWidth}`);
    // console.log(`availableWidth: ${availableWidth}, div.clientWidth: ${div.clientWidth}`);
  } while (div.clientWidth > availableWidth && padding > 2);
  div.style.display = displayStyle;
  div.style.width = widthStyle;
}
